import {
  format,
  isToday,
  isYesterday,
  differenceInDays,
  parseDate,
} from '@ardoq/date-time';
import type { HistoricChat } from '@ardoq/api-types';
import {
  LAST_30_DAYS_BUCKET_KEY,
  LAST_7_DAYS_BUCKET_KEY,
  PINNED_BUCKET_KEY,
  TODAY_BUCKET_KEY,
  YESTERDAY_BUCKET_KEY,
  type ChatMessageId,
  type HistoryBuckets,
} from './types';
import type { ClientRequest, UserEvent } from 'sync/types';
import { CHAT_MESSAGES_BODY_SCROLLABLE_ELEMENT_ID } from './consts';

export const getScrollView = () => {
  return document.getElementById(CHAT_MESSAGES_BODY_SCROLLABLE_ELEMENT_ID);
};

export const getFeedbackFormElementId = (messageId: ChatMessageId): string =>
  `feedback-form-${messageId}`;

/**
 * Stable, locale-independent bucket key for chats older than 30 days.
 * Format: `YYYY-MM` (e.g. `'2025-03'`). The component layer maps this back
 * to a localised display label.
 */
export const getMonthYearBucketKey = (date: Date): string =>
  format(date, 'yyyy-MM');

/** Matches the `YYYY-MM` keys produced by `getMonthYearBucketKey`. */
export const MONTH_YEAR_BUCKET_KEY_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * Returns the stable bucket key for a chat. Built-in bucket types use the
 * identifiers from `types.ts`; older chats use a locale-independent `YYYY-MM`
 * key. Keys are stable across locales — the component layer looks up display
 * labels in a separate `bucketLabels` map.
 */
export const getBucketKey = (chat: HistoricChat, now: Date): string => {
  const chatDate = parseDate(chat.created);

  if (chat.pinned) return PINNED_BUCKET_KEY;
  if (isToday(chatDate)) return TODAY_BUCKET_KEY;
  else if (isYesterday(chatDate)) return YESTERDAY_BUCKET_KEY;
  else if (differenceInDays(chatDate, now) >= -7) return LAST_7_DAYS_BUCKET_KEY;
  else if (differenceInDays(chatDate, now) >= -30)
    return LAST_30_DAYS_BUCKET_KEY;
  return getMonthYearBucketKey(chatDate);
};

export const getHistoryBuckets = (
  history: HistoricChat[],
  historySearchText: string,
  now: Date
): HistoryBuckets => {
  const sortedHistory = [...history].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    const dateA = parseDate(a.created);
    const dateB = parseDate(b.created);
    return dateB.getTime() - dateA.getTime();
  });

  const filteredHistory = sortedHistory.filter(chat => {
    const searchTerm = historySearchText.toLowerCase();
    const chatTitle = chat.title.toLowerCase();
    return chatTitle.includes(searchTerm);
  });

  return filteredHistory.reduce<HistoryBuckets>((buckets, chat) => {
    const bucketKey = getBucketKey(chat, now);

    if (!buckets[bucketKey]) {
      buckets[bucketKey] = [];
    }

    buckets[bucketKey].push(chat);
    return buckets;
  }, {});
};

export const getHighlightedTextParts = (
  text: string,
  searchText: string
): string[] => {
  // Avoid special characters in the highlighted string from interfering with the regex
  const escapeRegex = (str: string): string =>
    str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapeRegex(searchText)})`, 'gi');
  return text.split(regex);
};

export const isForActiveExchange = <Event extends { exchangeId: string }>([
  { exchangeId },
  { currentActiveRequest },
]: [Event, { currentActiveRequest: string | null }]): boolean =>
  exchangeId === currentActiveRequest;

export const isCurrentChatMessageEvent = (
  currentActiveRequest: string | null,
  event: UserEvent<ClientRequest<unknown>>
): boolean => {
  if (!currentActiveRequest) return false;
  return (
    currentActiveRequest === event.data.clientRequestId ||
    currentActiveRequest === event.data.exchangeId
  );
};
